import React, { useState, useEffect } from "react";
import { Subheading } from "../../../../design/heading";
import { Contact } from "@visitproof/types";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../../../firebase";
import CreateParentButton from "./CreateContactButton";
import ParentDetailContactListItem from "./ParentDetailContactListItem";
import ContactsLearnMore from "../../../../components/LearnMores/ContactsLearnMore";
import LoadingView from "../../../../components/LoadingView";

interface Props {
  parentId: string;
}

const ParentDetailContactList: React.FC<Props> = ({ parentId }) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Set up real-time listener for contacts
  useEffect(() => {
    if (!parentId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const contactsRef = collection(db, "parents", parentId, "contacts");

    const unsubscribe = onSnapshot(
      contactsRef,
      (snapshot) => {
        const contactsList: Contact[] = [];
        snapshot.forEach((doc) => {
          contactsList.push({ id: doc.id, ...doc.data() } as Contact);
        });
        // Sort client-side: emergency contacts first, then by name
        contactsList.sort((a, b) => {
          if (a.isEmergencyContact && !b.isEmergencyContact) return -1;
          if (!a.isEmergencyContact && b.isEmergencyContact) return 1;
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setContacts(contactsList);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching contacts:", err);
        setError("Failed to load contacts");
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [parentId]);

  if (loading) {
    return <LoadingView />;
  }

  if (error) {
    return (
      <div className="text-center text-red-600 dark:text-red-400 py-4">
        {error}
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-end justify-between w-full">
        <div className="flex items-center gap-1 mt-12">
          <Subheading>Contacts</Subheading>
          <ContactsLearnMore />
        </div>
        <div className="flex gap-4">
          <CreateParentButton parentId={parentId} />
        </div>
      </div>
      {contacts.length === 0 && (
        <p className="mt-4 text-sm text-zinc-500 dark:text-zinc-400">
          No contacts added yet.
        </p>
      )}
      {contacts.length > 0 && (
        <div className="mt-4 space-y-2">
          {contacts.map((contact) => (
            <ParentDetailContactListItem key={contact.id} contact={contact} />
          ))}
        </div>
      )}
    </>
  );
};

export default ParentDetailContactList;
